import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UmssIconComponent } from '../icon/icon.component';

export interface MenuItemData {
  value: unknown;
  label: string;
  iconName?: string; // Phosphor icon name
  description?: string;
  disabled?: boolean;
  selected?: boolean;
  type?: 'Default' | 'Destructive'; // Figma: Type property
  shortcut?: string;
}

@Component({
  selector: 'umss-menu-item',
  standalone: true,
  imports: [CommonModule, UmssIconComponent],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss'
})
export class UmssMenuItemComponent {
  /** Content props */
  @Input() itemData?: MenuItemData;
  @Input() label: string = '';
  @Input() value: unknown = null;
  @Input() iconName?: string; // Phosphor icon name
  @Input() description?: string;
  @Input() shortcut?: string;

  /** Variant props */
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() type: 'Default' | 'Destructive' = 'Default'; // Figma: Type (Default or Destructive)

  /** State props */
  @Input() disabled: boolean = false;
  @Input() selected: boolean = false;

  /** Event outputs */
  @Output() itemClick = new EventEmitter<MenuItemData | unknown>();
  @Output() itemHover = new EventEmitter<MenuItemData | unknown>();
  @Output() itemFocus = new EventEmitter<MenuItemData | unknown>();

  /** Computed properties */
  get displayLabel(): string {
    return this.itemData?.label || this.label;
  }

  get displayIconName(): string | undefined {
    return this.itemData?.iconName || this.iconName;
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Small':
        return '14px';
      case 'Regular':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '16px';
    }
  }

  get displayDescription(): string | undefined {
    return this.itemData?.description || this.description;
  }

  get displayShortcut(): string | undefined {
    return this.itemData?.shortcut || this.shortcut;
  }

  get isDisabled(): boolean {
    return this.itemData?.disabled || this.disabled;
  }

  get isSelected(): boolean {
    return this.itemData?.selected || this.selected;
  }

  get isDestructive(): boolean {
    return this.itemData?.type === 'Destructive' || this.type === 'Destructive';
  }

  get itemValue(): unknown {
    return this.itemData?.value || this.value;
  }

  /** Event handlers */
  onItemClick(): void {
    if (this.isDisabled) return;

    const emitValue = this.itemData || this.itemValue;
    this.itemClick.emit(emitValue);
  }

  onItemHover(): void {
    if (this.isDisabled) return;

    const emitValue = this.itemData || this.itemValue;
    this.itemHover.emit(emitValue);
  }

  onItemFocus(): void {
    if (this.isDisabled) return;

    const emitValue = this.itemData || this.itemValue;
    this.itemFocus.emit(emitValue);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.isDisabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onItemClick();
        break;
    }
  }
}
